import React from 'react';
import { Select, Input, Checkbox } from '@components/common';
import type { CronBuilderState, CronFrequency } from './cronUtils';
import {
  WEEKDAY_OPTIONS,
  buildCronExpression,
  humanizeCronExpression,
  isValidCronExpression,
} from './cronUtils';
import styles from './CronExpressionBuilder.module.css';

export interface CronExpressionBuilderProps {
  value: CronBuilderState;
  onChange: (next: CronBuilderState) => void;
  idPrefix: string;
}

const FREQUENCY_OPTIONS = [
  { value: 'every_n_minutes', label: 'Every N minutes' },
  { value: 'hourly', label: 'Hourly' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'custom', label: 'Custom cron' },
];

const INTERVAL_OPTIONS = [1, 2, 5, 10, 15, 30].map((value) => ({
  value: String(value),
  label: value === 1 ? 'Every minute' : `Every ${value} minutes`,
}));

function rangeOptions(start: number, end: number): { value: string; label: string }[] {
  const options = [];
  for (let value = start; value <= end; value += 1) {
    options.push({ value: String(value), label: String(value).padStart(2, '0') });
  }
  return options;
}

const HOUR_OPTIONS = rangeOptions(0, 23);
const MINUTE_OPTIONS = rangeOptions(0, 59);
const DAY_OPTIONS = rangeOptions(1, 31);

const CronExpressionBuilder: React.FC<CronExpressionBuilderProps> = ({ value, onChange, idPrefix }) => {
  const expression = buildCronExpression(value);
  const customInvalid = value.frequency === 'custom' && !isValidCronExpression(value.customExpression);

  const update = (patch: Partial<CronBuilderState>) => {
    onChange({ ...value, ...patch });
  };

  const toggleWeekday = (day: number) => {
    const hasDay = value.weekdays.includes(day);
    const weekdays = hasDay
      ? value.weekdays.filter((item) => item !== day)
      : [...value.weekdays, day].sort((a, b) => a - b);
    update({ weekdays: weekdays.length > 0 ? weekdays : [day] });
  };

  return (
    <div className={styles.builder} data-test-id={`${idPrefix}-cron-builder`}>
      <Select
        label="Frequency"
        value={value.frequency}
        onChange={(next) => update({ frequency: next as CronFrequency })}
        options={FREQUENCY_OPTIONS}
        dataTestId={`${idPrefix}-cron-frequency`}
      />

      {value.frequency === 'every_n_minutes' && (
        <Select
          label="Interval"
          value={String(value.intervalMinutes)}
          onChange={(next) => update({ intervalMinutes: Number(next) })}
          options={INTERVAL_OPTIONS}
          dataTestId={`${idPrefix}-cron-interval`}
        />
      )}

      {value.frequency === 'hourly' && (
        <Select
          label="Minute"
          value={String(value.minute)}
          onChange={(next) => update({ minute: Number(next) })}
          options={MINUTE_OPTIONS}
          dataTestId={`${idPrefix}-cron-minute`}
        />
      )}

      {(value.frequency === 'daily' || value.frequency === 'weekly' || value.frequency === 'monthly') && (
        <div className={styles.timeRow}>
          <Select
            label="Hour (UTC)"
            value={String(value.hour)}
            onChange={(next) => update({ hour: Number(next) })}
            options={HOUR_OPTIONS}
            dataTestId={`${idPrefix}-cron-hour`}
          />
          <Select
            label="Minute"
            value={String(value.minute)}
            onChange={(next) => update({ minute: Number(next) })}
            options={MINUTE_OPTIONS}
            dataTestId={`${idPrefix}-cron-minute`}
          />
        </div>
      )}

      {value.frequency === 'weekly' && (
        <fieldset className={styles.weekdays}>
          <legend>Days</legend>
          <div className={styles.weekdayList}>
            {WEEKDAY_OPTIONS.map((day) => (
              <Checkbox
                key={day.value}
                label={day.label}
                checked={value.weekdays.includes(day.value)}
                onChange={() => toggleWeekday(day.value)}
                dataTestId={`${idPrefix}-cron-weekday-${day.value}`}
              />
            ))}
          </div>
        </fieldset>
      )}

      {value.frequency === 'monthly' && (
        <Select
          label="Day of month"
          value={String(value.dayOfMonth)}
          onChange={(next) => update({ dayOfMonth: Number(next) })}
          options={DAY_OPTIONS}
          dataTestId={`${idPrefix}-cron-day`}
        />
      )}

      {value.frequency === 'custom' && (
        <Input
          label="Cron expression"
          value={value.customExpression}
          onChange={(event) => update({ customExpression: event.target.value })}
          error={customInvalid ? 'Enter a valid 5-field cron expression' : undefined}
          dataTestId={`${idPrefix}-cron-custom`}
        />
      )}

      <p className={styles.summary} data-test-id={`${idPrefix}-cron-summary`}>
        {humanizeCronExpression(expression)}
      </p>
    </div>
  );
};

export default CronExpressionBuilder;
